import { useState, useEffect, useCallback, useRef } from 'react';

export function useWakeLock() {
    const isSupported = 'wakeLock' in navigator;

    // State: Enabled (User Preference)
    const [enabled, setEnabledState] = useState(() => {
        try {
            return localStorage.getItem('bible_wakelock') === 'true';
        } catch {
            return false;
        }
    });

    // State: Status (Actual)
    const [isActive, setIsActive] = useState(false);
    const [error, setError] = useState<string | null>(null);
    const sentinelRef = useRef<WakeLockSentinel | null>(null);

    // Persistence
    useEffect(() => {
        try {
            localStorage.setItem('bible_wakelock', String(enabled));
        } catch { /* ignore */ }
    }, [enabled]);

    // Action: Request
    const requestLock = useCallback(async () => {
        if (!isSupported) return;
        // If we already have a valid sentinel, do nothing
        if (sentinelRef.current && !sentinelRef.current.released) return;

        try {
            const sentinel = await navigator.wakeLock.request('screen');
            sentinelRef.current = sentinel;
            setIsActive(true); // Optimistic? No, explicit.
            setError(null);

            // Listener for release (e.g. system released it, or we switched tabs)
            sentinel.addEventListener('release', () => {
                console.log('[WakeLock] Released (Event)');
                // Only update state if this sentinel is still the current one
                if (sentinelRef.current === sentinel) {
                    setIsActive(false);
                    sentinelRef.current = null;
                }
            });

            console.log('[WakeLock] Acquired');
        } catch (err: any) {
            console.error('[WakeLock] Request failed:', err);
            setError(`${err.name}: ${err.message}`);
            setIsActive(false);
            // We do NOT flip 'enabled' to false. User wants it, we just failed.
        }
    }, [isSupported]);

    // Action: Release
    const releaseLock = useCallback(async () => {
        const s = sentinelRef.current;
        if (s) {
            try {
                await s.release();
            } catch (e) {
                console.error('[WakeLock] Release error', e);
            }
            sentinelRef.current = null;
        }
        setIsActive(false);
    }, []);

    // Lifecycle Manager
    useEffect(() => {
        const handleVisibilityChange = () => {
            if (document.visibilityState === 'visible' && enabled) {
                // Re-acquire if we came back and want it enabled
                requestLock();
            }
        };

        document.addEventListener('visibilitychange', handleVisibilityChange);
        document.addEventListener('pagehide', releaseLock); // Clean exit?

        if (enabled) {
            requestLock();
        } else {
            releaseLock();
        }

        return () => {
            document.removeEventListener('visibilitychange', handleVisibilityChange);
            document.removeEventListener('pagehide', releaseLock);
            releaseLock();
        };
    }, [enabled, requestLock, releaseLock]);

    return {
        enabled,
        setEnabled: setEnabledState,
        isSupported,
        isActive,
        error
    };
}
